/*
 * Orlana advertorial — small, self-contained behaviors for "ADV - ..." sections.
 * No external libraries. Each <adv-countdown> instance owns its own interval,
 * started in connectedCallback and cleared in disconnectedCallback, so multiple
 * countdowns on one page stay independent and the Theme Editor's add/remove/reload
 * cycle never leaks timers or double-initializes.
 *
 * Two modes, both computed as a pure function of the current real time (never
 * stored per-visitor/session state), so a refresh or a new visitor never resets
 * the clock and everyone sees the same remaining time:
 *   - "once":  a single absolute target instant, baked into data-end server-side.
 *   - "daily": the next occurrence of a time-of-day in the SHOP's timezone,
 *              recomputed on every tick from data-reset-hour/minute and the
 *              shop's UTC offset at page-render time (data-shop-offset).
 */
(function () {
  if (customElements.get('adv-countdown')) return;

  function parseOffset(str) {
    // Shopify's `date: '%z'` format: "+0200" / "-0500"
    if (!str || str.length < 5) return 0;
    var sign = str.charAt(0) === '-' ? -1 : 1;
    var hours = parseInt(str.slice(1, 3), 10) || 0;
    var minutes = parseInt(str.slice(3, 5), 10) || 0;
    return sign * (hours * 60 + minutes);
  }

  class AdvCountdown extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode === 'daily' ? 'daily' : 'once';
      this.expiredBehavior = this.dataset.expiredBehavior || 'hide_timer';
      this.daysEl = this.querySelector('[data-adv-cd-days]');
      this.hoursEl = this.querySelector('[data-adv-cd-hours]');
      this.minutesEl = this.querySelector('[data-adv-cd-minutes]');
      this.secondsEl = this.querySelector('[data-adv-cd-seconds]');
      this.numbersEl = this.querySelector('[data-adv-cd-numbers]');
      this.expiredTextEl = this.querySelector('[data-adv-cd-expired-text]');

      if (this.mode === 'daily') {
        this.resetHour = parseInt(this.dataset.resetHour, 10) || 0;
        this.resetMinute = parseInt(this.dataset.resetMinute, 10) || 0;
        this.shopOffsetMinutes = parseOffset(this.dataset.shopOffset);
        this.restart = this.dataset.restart === 'true';
        this.targetTime = this.nextDailyTarget();
      } else {
        this.targetTime = Date.parse(this.dataset.end);
      }

      if (isNaN(this.targetTime)) return;

      this.tick();
      this.interval = setInterval(this.tick.bind(this), 1000);
    }

    disconnectedCallback() {
      if (this.interval) clearInterval(this.interval);
    }

    // The next moment the shop-local clock reads resetHour:resetMinute,
    // computed from the CURRENT instant — never from a stored "start time".
    nextDailyTarget() {
      var offsetMs = this.shopOffsetMinutes * 60000;
      var nowMs = Date.now();
      var shopLocalNow = new Date(nowMs + offsetMs);
      var targetShopLocal = new Date(Date.UTC(
        shopLocalNow.getUTCFullYear(),
        shopLocalNow.getUTCMonth(),
        shopLocalNow.getUTCDate(),
        this.resetHour,
        this.resetMinute,
        0,
        0
      ));
      var targetUtcMs = targetShopLocal.getTime() - offsetMs;

      if (targetUtcMs <= nowMs) {
        // Today's reset already passed: move the shop-local date forward a day
        // and recompute, rather than just adding 24h (which would be wrong
        // across a DST transition).
        var nextDay = new Date(shopLocalNow.getTime());
        nextDay.setUTCDate(nextDay.getUTCDate() + 1);
        targetShopLocal = new Date(Date.UTC(
          nextDay.getUTCFullYear(),
          nextDay.getUTCMonth(),
          nextDay.getUTCDate(),
          this.resetHour,
          this.resetMinute,
          0,
          0
        ));
        targetUtcMs = targetShopLocal.getTime() - offsetMs;
      }

      return targetUtcMs;
    }

    tick() {
      var remaining = this.targetTime - Date.now();

      if (remaining <= 0) {
        if (this.mode === 'daily' && this.restart) {
          this.targetTime = this.nextDailyTarget();
          this.tick();
          return;
        }
        clearInterval(this.interval);
        this.onExpire();
        return;
      }

      var totalSeconds = Math.floor(remaining / 1000);
      var days = 0;
      var hours;
      if (this.daysEl) {
        days = Math.floor(totalSeconds / 86400);
        hours = Math.floor((totalSeconds % 86400) / 3600);
      } else {
        // No separate "days" unit in this markup (e.g. the announcement bar's
        // HH:MM:SS display): roll everything into hours instead of wrapping at 24.
        hours = Math.floor(totalSeconds / 3600);
      }
      var minutes = Math.floor((totalSeconds % 3600) / 60);
      var seconds = totalSeconds % 60;

      if (this.daysEl) this.daysEl.textContent = this.pad(days);
      if (this.hoursEl) this.hoursEl.textContent = this.pad(hours);
      if (this.minutesEl) this.minutesEl.textContent = this.pad(minutes);
      if (this.secondsEl) this.secondsEl.textContent = this.pad(seconds);
    }

    onExpire() {
      if (this.expiredBehavior === 'hide_section') {
        this.setAttribute('hidden', 'hidden');
      } else if (this.expiredBehavior === 'show_text') {
        if (this.numbersEl) this.numbersEl.setAttribute('hidden', 'hidden');
        if (this.expiredTextEl) this.expiredTextEl.removeAttribute('hidden');
      } else {
        if (this.numbersEl) this.numbersEl.setAttribute('hidden', 'hidden');
      }
    }

    pad(value) {
      return value < 10 ? '0' + value : String(value);
    }
  }

  customElements.define('adv-countdown', AdvCountdown);
})();

/*
 * <adv-video> — wraps an article/header video so a browser-blocked autoplay
 * attempt (iOS Low Power Mode, battery saver, an unmuted autoplay request,
 * etc.) always leaves the visitor a working play button instead of a dead,
 * silently-paused video. Calling play() ourselves (rather than trusting the
 * autoplay attribute) is what gives us a promise to catch the rejection on.
 */
(function () {
  if (customElements.get('adv-video')) return;

  class AdvVideo extends HTMLElement {
    connectedCallback() {
      var video = this.querySelector('video');
      var button = this.querySelector('[data-adv-video-play]');
      if (!video) return;

      if (button) {
        button.addEventListener('click', function () {
          video.play().then(function () {
            button.hidden = true;
          }).catch(function () {});
        });
      }

      if (video.hasAttribute('autoplay')) {
        var playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(function () {
            if (button) button.hidden = false;
          });
        }
      }
    }
  }

  customElements.define('adv-video', AdvVideo);
})();
